import React, { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import ComboboxInput from "../inputs/ComboboxInput";
import { mapToOptions } from "../../lib/functions";
import {
  useGetAllPlateLetters,
  useGetAllPlateSource,
} from "../../hooks/common/query";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import { USER_ROLES_IDS, UserAppointment } from "../../constants/common";
import useAuthStore from "../../store/Auth";

interface VehicleInformationProps {
  control: any;
  errors: any;
  clearErrors: any;
  userAppointmentDetails?: UserAppointment;
  hideCardNumber?: boolean;
  isVehicleInfoVisible?: boolean;
}

const VehicleInformationCard = ({
  control,
  errors,
  userAppointmentDetails,
  clearErrors,
  hideCardNumber = false,
  isVehicleInfoVisible,
}: VehicleInformationProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const user = useAuthStore((state) => state.user);

  const { t } = useTranslation();

  const { data: allPlateSource } = useGetAllPlateSource();
  const { data: allPlateLetters } = useGetAllPlateLetters();

  const plateLetters = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allPlateLetters),
    [allPlateLetters]
  );

  const plateSource = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allPlateSource),
    [allPlateSource]
  );

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {!hideCardNumber && `${t("2")}.`} {t("vehicleInformation")}
          </h1>
        }
      >
        <div className="flex flex-col gap-2 lg:gap-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4">
            <Controller
              name="plateSource"
              control={control}
              render={({ field: { name, onChange, value } }) => {
                return (
                  <ComboboxInput
                    options={plateSource}
                    onChange={(value) => {
                      onChange(value);
                      clearErrors(name);
                    }}
                    value={
                      plateSource?.find((item) => item.value === value)
                        ?.labelEn ||
                      plateSource?.find(
                        (item) =>
                          item.value === userAppointmentDetails?.plateSource
                      )?.labelEn ||
                      ""
                    }
                    error={errors?.[name]?.message}
                    label={t("plateType")}
                    required={isVehicleInfoVisible}
                    readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                  />
                );
              }}
            />

            <Controller
              name="plateLetter1"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={plateLetters}
                  value={
                    plateLetters?.find((item) => item.value === value)
                      ?.labelEn ||
                    plateLetters?.find(
                      (item) =>
                        item.value === userAppointmentDetails?.plateLetter1
                    )?.labelEn ||
                    ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("plateLetter1")}
                  required={isVehicleInfoVisible}
                  readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                />
              )}
            />

            <Controller
              name="plateLetter2"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={plateLetters}
                  value={
                    plateLetters?.find((item) => item.value === value)
                      ?.labelEn ||
                    plateLetters?.find(
                      (item) =>
                        item.value === userAppointmentDetails?.plateLetter2
                    )?.labelEn ||
                    ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("plateLetter2")}
                  required={isVehicleInfoVisible}
                  readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                />
              )}
            />

            <Controller
              name="plateLetter3"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={plateLetters}
                  value={
                    plateLetters?.find((item) => item.value === value)
                      ?.labelEn ||
                    plateLetters?.find(
                      (item) =>
                        item.value === userAppointmentDetails?.plateLetter3
                    )?.labelEn ||
                    ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("plateLetter3")}
                  required={isVehicleInfoVisible}
                  readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                />
              )}
            />

            <Controller
              name="plateNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("plateNumber")}
                  value={value ?? userAppointmentDetails?.plateNumber ?? ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={isVehicleInfoVisible}
                  readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                  isInfoToolTip
                  infoToolTip={<p>{t("enterPlateNumberUpto6Digits")}</p>}
                />
              )}
            />
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default React.memo(VehicleInformationCard);
